import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { connectorsApi, ConnectorKind, ConnectorRow } from '@/lib/connectors';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { HelpHint } from '@/components/app/HelpHint';
import { useDialogs } from '@/components/app/DialogProvider';
import { InlineAlert } from '@/components/app/Modal';

export function ConnectorsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { confirm, alert } = useDialogs();
  const [regionFilter, setRegionFilter] = useState<'all' | 'global' | 'jp'>('all');
  const onMutationError = () =>
    alert({ title: t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' }) as string });

  const { data: catalog = [] } = useQuery({
    queryKey: ['connector-catalog'],
    queryFn: () => connectorsApi.catalog(),
  });

  const { data: rows = [] } = useQuery({
    queryKey: ['connectors', wsId],
    queryFn: () => connectorsApi.list(wsId!),
    enabled: !!wsId,
  });

  const create = useMutation({
    mutationFn: (kind: ConnectorKind) => connectorsApi.create(wsId!, kind),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['connectors', wsId] });
      window.location.href = connectorsApi.startUrl(row.id);
    },
    onError: onMutationError,
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => connectorsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['connectors', wsId] }),
    onError: onMutationError,
  });

  const sync = useMutation({
    mutationFn: (id: string) => connectorsApi.sync(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['connectors', wsId] }),
    onError: onMutationError,
  });
  const confirmDisconnect = async (r: ConnectorRow) => {
    const ok = await confirm({
      title: t('connectors.disconnect_confirm', { defaultValue: 'Disconnect {{name}}? Credentials are wiped and syncing stops. Already-indexed documents remain.', name: r.displayName }) as string,
      confirmLabel: t('connectors.disconnect', { defaultValue: 'Disconnect' }) as string,
    });
    if (ok) {
      disconnect.mutate(r.id);
    }
  };

  const filtered = catalog.filter((c) => regionFilter === 'all' || c.region === regionFilter);
  const queryError = searchParams.get('error');
  const querySuccess = searchParams.get('ok');

  return (
    <AppShell
      title={t('connectors.title', { defaultValue: 'Connectors' })}
      description={t('connectors.subtitle', { defaultValue: 'Authorize and manage data sources flowing into this workspace.' }) as string}
      maxWidth="max-w-5xl"
    >
      <AdminSiblingNav wsId={wsId!} current="connectors" />
      <div className="mb-4"><HelpHint page="connectors" /></div>
      <div className="space-y-6">
        {queryError && <InlineAlert tone="error">{queryError}</InlineAlert>}
        {querySuccess && (
          <InlineAlert tone="success">
            {t('connectors.connected', { defaultValue: 'Connector authorized.' })}
          </InlineAlert>
        )}

        {rows.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-ink-900 mb-3">
              {t('connectors.active', { defaultValue: 'Active' })} ({rows.length})
            </h2>
            <ul className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="card-paper px-4 py-3 flex items-center justify-between text-sm"
                >
                  <div>
                    <div className="font-medium text-ink-900">{r.label}</div>
                    <div className="text-xs text-ink-500">{r.displayName}</div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <StatusBadge status={r.status} />
                    {r.lastError && <span className="text-warn-600 max-w-md truncate">{r.lastError}</span>}
                    {!r.hasCredentials ? (
                      <a
                        href={connectorsApi.startUrl(r.id)}
                        className="btn-press px-3 py-1.5 text-xs"
                      >
                        {r.status === 'error'
                          ? t('connectors.reauthorize', { defaultValue: 'Reconnect' })
                          : t('connectors.authorize', { defaultValue: 'Authorize' })}
                      </a>
                    ) : (
                      <button
                        onClick={() => sync.mutate(r.id)}
                        disabled={sync.isPending || r.status === 'syncing'}
                        className="text-xs text-accent-600 hover:text-accent-700 disabled:opacity-40"
                      >
                        {sync.isPending && sync.variables === r.id
                          ? t('connectors.syncing', { defaultValue: 'Syncing…' })
                          : t('connectors.sync_now', { defaultValue: 'Sync now' })}
                      </button>
                    )}
                    <button
                      onClick={() => confirmDisconnect(r)}
                      disabled={disconnect.isPending}
                      className="text-xs text-rose-600 hover:text-rose-700 disabled:opacity-40"
                    >
                      {t('connectors.disconnect', { defaultValue: 'Disconnect' })}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink-900">
              {t('connectors.add', { defaultValue: 'Add a connector' })}
            </h2>
            <div className="flex items-center gap-1 text-xs">
              {(['all', 'global', 'jp'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRegionFilter(r)}
                  className={`px-2.5 py-1 rounded-md transition ${
                    regionFilter === r
                      ? 'bg-accent-500 text-white shadow-press'
                      : 'bg-paper-100 text-ink-600 hover:text-ink-900'
                  }`}
                >
                  {r === 'jp' ? 'JP' : r === 'global' ? 'Global' : 'All'}
                </button>
              ))}
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((c) => (
              <li key={c.kind} className="card-paper p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-ink-900">{c.label}</div>
                    <div className="text-xs text-ink-500 mt-1 leading-snug">{c.description}</div>
                    <a
                      href={c.partnerDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-[10px] text-accent-600 hover:underline"
                    >
                      {t('connectors.docs', { defaultValue: 'Partner docs ↗' })}
                    </a>
                  </div>
                  <button
                    onClick={() => create.mutate(c.kind)}
                    disabled={create.isPending}
                    className="btn-press px-3 py-1.5 text-xs whitespace-nowrap disabled:opacity-50"
                  >
                    {t('connectors.connect', { defaultValue: 'Connect' })}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}

function StatusBadge({ status }: { status: ConnectorRow['status'] }) {
  const colors: Record<ConnectorRow['status'], string> = {
    pending: 'bg-paper-100 text-ink-600',
    authorizing: 'bg-accent-50 text-accent-700',
    active: 'bg-emerald-50 text-emerald-700',
    syncing: 'bg-accent-50 text-accent-700',
    error: 'bg-warn-500/10 text-warn-600',
    disabled: 'bg-paper-100 text-ink-400',
  };
  return <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${colors[status]}`}>{status}</span>;
}
